<template>
  <header class="app-header">
    <div class="logo">
      <img v-if="!logoError" class="logo-img" :src="logoUrl" alt="RouteBox" @error="logoError = true" />
    </div>
      <nav>
      <ul>
        <li v-if="userPermissions.home">
          <button @click="navigateTo('/home')" :class="{ active: $route.path === '/home' }">Home</button>
        </li>
        <li v-if="userPermissions.vehicles">
          <button @click="navigateTo('/vehicles')" :class="{ active: $route.path === '/vehicles' }">Vehicles</button>
        </li>
        <li v-if="userPermissions.locations">
          <button @click="navigateTo('/locations')" :class="{ active: $route.path === '/locations' }">Locations</button>
        </li>
        <li v-if="userPermissions.history">
          <button @click="navigateTo('/history')" :class="{ active: $route.path === '/history' }">History</button>
        </li>
        <li v-if="userPermissions.analytics">
          <button @click="navigateTo('/analytics')" :class="{ active: $route.path === '/analytics' }" class="analytics-btn">RouteBox Analytics</button>
        </li>
        <li>
          <button @click="navigateTo('/ai-tools')" :class="{ active: $route.path === '/ai-tools' }" class="ai-tools-btn">AI Tools</button>
        </li>
      </ul>
    </nav>

    <div class="user-section">
      <div class="user-info" v-if="user">
        <span 
          class="user-name clickable" 
          @click="navigateTo('/profile')" 
          title="View Profile"
        >
          {{ user.displayName || user.email }}
        </span>
        <div class="user-avatar">
          <img v-if="user.photoURL" :src="user.photoURL" :alt="user.displayName || 'User'" />
          <div v-else class="avatar-placeholder">
            {{ (user.displayName || user.email || 'U').charAt(0).toUpperCase() }}
          </div>
        </div>
      </div>
      <button @click="handleLogout" class="logout-button" title="Sign Out">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16,17 21,12 16,7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
      </button>
    </div>
  </header>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AuthService from '../services/AuthService'

export default {
  name: 'AppHeader',
  setup() {
    const router = useRouter()
    const user = ref(null)
         const logoUrl = '/logo_routebox_2_white.svg'
    const logoError = ref(false)
    const userPermissions = ref({
      home: false,
      vehicles: false,
      locations: false,
      analytics: false,
      history: false,
      ai_tools: false
    })

    const loadUserPermissions = async () => {
      if (user.value) {
        try {
          const permissions = await AuthService.getUserPermissions(user.value.uid)
          userPermissions.value = permissions
        } catch (error) {
          console.error('Error loading user permissions:', error)
        }
      }
    }

    onMounted(() => {
      // Get current user
      user.value = AuthService.getCurrentUser()
      if (user.value) {
        loadUserPermissions()
      }
      
      // Listen for auth state changes
      AuthService.onAuthStateChange((currentUser) => {
        user.value = currentUser
        if (currentUser) {
          loadUserPermissions()
        } else {
          userPermissions.value = {
            home: false,
            vehicles: false,
            locations: false,
            analytics: false,
            history: false,
            ai_tools: false
          }
        }
      })
    })

    const navigateTo = (path) => {
      router.push(path)
    }

    const handleLogout = async () => {
      try {
        await AuthService.signOut()
        router.push('/login')
      } catch (error) {
        console.error('Error signing out:', error)
      }
    }

    return {
      user,
      userPermissions,
      navigateTo,
      handleLogout,
      logoUrl,
      logoError
    }
  }
}
</script>

<style scoped>
.app-header {
  background-color: #2c3e50;
  color: white;
  padding: 0.5rem 1rem 0.5rem 1.5rem; /* increase left padding to align with left panel */
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.logo {
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  font-weight: bold;
  padding-left: 0.5rem; /* match left panel h2 padding */
}

.logo span {
  font-family: 'Sansation', sans-serif;
  letter-spacing: 0.5px;
  font-weight: bold;
}

.logo-img {
  height: 40px;
  margin-right: 10px;
  transition: all 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
  opacity: 0.9;
}

.analytics-btn {
  background-color: #42b983 !important;
  color: white !important;
  font-weight: bold;
}

.ai-tools-btn {
  background-color: #9b59b6 !important;
  color: white !important;
  font-weight: bold;
}

nav ul {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

nav ul li {
  margin-left: 1rem;
}

nav button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.4rem 0.8rem;
  transition: all 0.2s;
  border-radius: 4px;
  position: relative;
}

nav button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

nav button.active {
  background-color: rgba(255, 255, 255, 0.15);
}

nav button.active:after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 3px;
  background-color: #3498db;
  border-radius: 3px;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.user-name.clickable {
  cursor: pointer;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.logout-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logout-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

@media (max-width: 768px) {
  .user-name {
    display: none;
  }
  
  nav ul {
    gap: 0.5rem;
  }
  
  nav ul li {
    margin-left: 0.5rem;
  }
  
  nav button {
    padding: 0.3rem 0.6rem;
    font-size: 0.85rem;
  }
}
</style>
